import React, { useState, useEffect, useRef } from 'react'
import c from 'classnames'
import { Range } from 'rc-slider'
import shuffle from 'lodash/shuffle'

import Tag from 'discada/dist/Tag'
import Icon from 'discada/dist/Icon'

import { InventoryCard } from 'components/Chip/InventoryChip'

import './widgets.scss'

export function InventoryCardList(props) {
  let vehicleIds = props.vehicleIds

  return (
    <VlpSection
      title={props.title}
      className={c('inventory-card-list', props.className)}
    >
      <ScrollingList {...props} className="inventory-card-scroll-wrapper">
        {shuffle(vehicleIds)
          .slice(0, 15)
          .map((id, i) => (
            <Fader key={i} className="inventory-chip-wrapper">
              <InventoryCard id={id} />
            </Fader>
          ))}
      </ScrollingList>
      {props.children}
    </VlpSection>
  )
}

export function Fader(props) {
  const [isInView, setIsInView] = useState(false),
    {
      className,
      rootMargin = '0px',
      fadeDelay = 10,
      renderChildren,
      children,
      style,
    } = props,
    ref = useRef()

  let observer = undefined

  try {
    if (window.IntersectionObserver) {
      //eslint-disable-next-line
      observer = new IntersectionObserver(
        ([entry]) => {
          setTimeout(() => {
            setIsInView(entry.isIntersecting)
          }, fadeDelay)
        },
        { rootMargin },
      )
    }
  } catch (e) {}

  useEffect(() => {
    observer && observer.observe(ref.current)

    return () => observer && observer.disconnect()
  }, [ref, observer, fadeDelay])

  let visibleInViewport = isInView || observer === undefined,
    faderClass = visibleInViewport ? 'fader-in' : 'fader-out',
    childrenOk = renderChildren || visibleInViewport

  return (
    <div className={c('fader', className, faderClass)} ref={ref} style={style}>
      {childrenOk && children}
    </div>
  )
}

export function VlpSection(props) {
  return (
    <div
      {...props}
      className={c('vlp-section', props.className, {
        'with-air': props.withAir,
      })}
    >
      {props.title && <h3>{props.title}</h3>}
      <div className="vlp-section-body">{props.children}</div>
    </div>
  )
}

export function ScrollButton(props) {
  let {
      direction,
      setScrollLeft,
      container = {},
      scrollLeft = 0,
      buttonSize = 'large',
    } = props,
    mod = direction === 'left' ? -1 : 1,
    icon = `chevron-circle-${direction}`,
    offsetWidth = container.offsetWidth || 0,
    scrollWidth = container.scrollWidth || 0,
    newScrollLeft = Math.min(
      scrollWidth,
      scrollLeft + offsetWidth * 0.5 * mod + mod,
    ),
    hide =
      (scrollLeft === 0 && direction === 'left') ||
      (scrollLeft >= scrollWidth - offsetWidth && direction === 'right')

  return (
    <div
      className={c(
        'scrolling-list-button',
        `scrolling-list-button-${direction}`,
        `scrolling-list-button-${buttonSize}`,
        {
          'scrolling-list-button-hide': hide,
        },
      )}
    >
      <Icon
        fixedWidth
        icon={icon}
        onClick={() => setScrollLeft(Math.max(0, newScrollLeft))}
      />
    </div>
  )
}

export function ScrollingList(props) {
  let { children, className } = props,
    container = useRef(),
    [scrollLeft, setScrollLeft] = useState(0),
    [, updateState] = React.useState(),
    forceUpdate = React.useCallback(() => updateState({}), []) // trick to get the ref to populate in cases where children is static

  useEffect(() => {
    container.current.scrollLeft = scrollLeft
    forceUpdate()
  }, [container, scrollLeft, children, forceUpdate])

  return (
    <div className={c('scrolling-list', className)}>
      <div ref={container} className="scrolling-list-body">
        {children}
      </div>
      <ScrollButton
        {...props}
        direction="left"
        container={container.current}
        scrollLeft={scrollLeft}
        setScrollLeft={setScrollLeft}
      />
      <ScrollButton
        {...props}
        direction="right"
        container={container.current}
        scrollLeft={scrollLeft}
        setScrollLeft={setScrollLeft}
      />
    </div>
  )
}

export function PickerGroup(props) {
  let { className, children, title } = props

  return (
    <div className={c('picker-group', className)}>
      <h4 className={c('picker-group-title')}>{title}</h4>
      <div className={c('picker-group-body')}>{children}</div>
    </div>
  )
}

export function PickerOption(props) {
  let { className, children, control, onClick = () => {}, selected } = props

  return (
    <span
      className={c('picker-option', className, {
        'picker-option-selected': selected,
      })}
      onClick={onClick}
    >
      <Tag className="is-light" rounded>
        {control && <span className="picker-option-control">{control}</span>}
        <span className="picker-option-label">{children}</span>
        <Icon icon="plus" />
      </Tag>
    </span>
  )
}

export function PickerControl({ selected }) {
  return <input type="checkbox" checked={selected} />
}

export function Picker(props) {
  let { options = {}, control, className, title } = props,
    picks = Array.isArray(options)
      ? options.reduce((ret, o) => ({ ...ret, [o]: o }), {})
      : options,
    pickKeys = Object.keys(picks).sort()

  return (
    <PickerGroup className={c('simple-picker', className)} title={title}>
      {pickKeys.map((o, i) => (
        <PickerOption
          key={i}
          className={`picker-option-${o}`}
          control={control && control(o)}
        >
          {picks[o]}
        </PickerOption>
      ))}
    </PickerGroup>
  )
}

export function RangePicker(props) {
  let { className, title, min, max } = props

  return (
    <PickerGroup className={c('range-picker', className)} title={title}>
      <Range min={min} max={max} />
    </PickerGroup>
  )
}

export function TagPicker(props) {
  let { className, title } = props

  return (
    <PickerGroup
      className={c('range-picker', className)}
      title={title}
    ></PickerGroup>
  )
}

///////////////////////////////////////////

export function ColorPicker(props) {
  let colors = [
      'red',
      'green',
      'blue',
      'black',
      'silver',
      'orange',
      'white',
      'brown',
    ],
    colorDisplay = o => (
      <span className="color-option">
        <span
          className="color-picker-swatch"
          style={{ backgroundColor: o, color: o }}
        />
        <span className="color-picker-name">{o}</span>
      </span>
    )

  return (
    <Picker
      {...props}
      className="color-picker"
      options={colors.reduce(
        (ret, c) => ({
          ...ret,
          [c]: colorDisplay(c),
        }),
        {},
      )}
    />
  )
}
